import { useAuthStore } from '../store/useAuthStore'

type AnalyticsProperties = Record<string, any>

declare global {
    interface Window {
        analytics?: any
        gtag?: (...args: any[]) => void
        fbq?: (...args: any[]) => void
    }
}

const currency = 'MXN'
const source = 'storefront'
const anonymousIdKey = 'canvasexpress_anonymous_id'
const sessionIdKey = 'canvasexpress_session_id'
const cartIdKey = 'canvasexpress_cart_id'

const ga4EventMap: Record<string, string> = {
    'Page Viewed': 'page_view',
    'Product Viewed': 'view_item',
    'Product Added': 'add_to_cart',
    'Product Removed': 'remove_from_cart',
    'Cart Viewed': 'view_cart',
    'Checkout Started': 'begin_checkout',
    'Payment Info Entered': 'add_payment_info',
    'Order Completed': 'purchase'
}

const metaEventMap: Record<string, string> = {
    'Product Viewed': 'ViewContent',
    'Product Added': 'AddToCart',
    'Checkout Started': 'InitiateCheckout',
    'Order Completed': 'Purchase'
}

const metaPurchaseStoragePrefix = 'canvasexpress_meta_purchase_'

const isBrowser = () => typeof window !== 'undefined'

const createId = () => {
    if (isBrowser() && window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID()
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const readOrCreateStorageValue = (key: string) => {
    if (!isBrowser()) return ''

    const existing = window.localStorage.getItem(key) || window.sessionStorage.getItem(key)
    if (existing) return existing

    const value = createId()
    const storage = key === sessionIdKey ? window.sessionStorage : window.localStorage
    storage.setItem(key, value)

    return value
}

const getGaClientId = () => {
    if (!isBrowser()) return ''

    const gaCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('_ga='))

    if (!gaCookie) return ''

    return gaCookie.split('.').slice(-2).join('.')
}

export const getTrackingContext = () => {
    const user = useAuthStore.getState().user

    return {
        anonymous_id: readOrCreateStorageValue(anonymousIdKey),
        user_id: user?.id,
        cart_id: readOrCreateStorageValue(cartIdKey),
        session_id: readOrCreateStorageValue(sessionIdKey),
        ga_client_id: getGaClientId(),
        page_url: isBrowser() ? window.location.href : '',
        path: isBrowser() ? window.location.pathname : '',
        referrer: isBrowser() ? document.referrer : '',
        currency,
        source
    }
}

export const getTrackingIds = () => {
    const context = getTrackingContext()

    return {
        anonymousId: context.anonymous_id,
        sessionId: context.session_id,
        cartId: context.cart_id,
        gaClientId: context.ga_client_id,
        userId: context.user_id
    }
}

const toSnakeCase = (eventName: string) => {
    return eventName
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase()
}

const normalizeItem = (item: any = {}) => {
    const category = item.category?.name || item.categoryName || item.category
    const subcategory = item.subcategory?.name || item.subcategoryName || item.subcategory
    const categoryType = item.category?.type || item.categoryType
    const price = Number(item.unitPrice || item.price || 0)
    const quantity = Number(item.quantity || 1)

    return {
        item_id: item.productId || item.product_id || item.id,
        product_id: item.productId || item.product_id,
        variant_id: item.variantId || item.variant_id,
        item_name: item.name || item.photoName || item.productName,
        category,
        subcategory,
        category_type: categoryType,
        artist_id: item.artistId || (categoryType === 'ARTIST' ? item.categoryId : undefined),
        artist_name: item.artistName || (categoryType === 'ARTIST' ? category : undefined),
        collection_id: item.collectionId || (categoryType === 'ARTIST' ? item.subcategoryId : undefined),
        collection_name: item.collectionName || (categoryType === 'ARTIST' ? subcategory : undefined),
        material: item.material,
        size: item.size,
        frame: item.frame,
        marialuisa: item.marialuisa,
        price,
        quantity,
        discount: Number(item.discount || item.automaticDiscount || 0) + Number(item.codeDiscount || 0),
        currency
    }
}

export const normalizeAnalyticsItem = normalizeItem

export const normalizeAnalyticsItems = (items: any[] = []) => items.map(normalizeItem)

const getEventPayload = (properties: AnalyticsProperties = {}) => {
    const items = Array.isArray(properties.items)
        ? normalizeAnalyticsItems(properties.items)
        : properties.item
            ? [normalizeItem(properties.item)]
            : undefined

    return {
        ...getTrackingContext(),
        ...properties,
        ...(items ? { items } : {})
    }
}

const toGa4Payload = (properties: AnalyticsProperties) => {
    const payload = { ...properties }

    if (payload.items) {
        payload.items = payload.items.map((item: any) => ({
            item_id: item.item_id || item.product_id,
            item_name: item.item_name,
            item_category: item.category,
            item_category2: item.subcategory,
            item_variant: [item.material, item.size, item.frame, item.marialuisa].filter(Boolean).join(' / '),
            price: item.price,
            quantity: item.quantity,
            discount: item.discount
        }))
    }

    return payload
}

const logAnalyticsError = (service: string, error: any) => {
    if (process.env.NODE_ENV !== 'production') {
        console.warn(`Analytics ${service} failed`, error)
    }
}

const dispatchMeta = (eventName: string, payload: AnalyticsProperties) => {
    const metaEventName = metaEventMap[eventName]
    if (!metaEventName || typeof window.fbq !== 'function') return

    const items = Array.isArray(payload.items) ? payload.items : []
    const contents = items
        .filter((item: any) => Boolean(item.product_id || item.item_id))
        .map((item: any) => ({
            // The Meta catalog feed uses the base product UUID, not the variant UUID.
            id: `${item.product_id || item.item_id}`,
            quantity: Number(item.quantity || 1),
            item_price: Number(item.price || 0)
        }))
    const contentIds = contents.map((item: any) => item.id)
    const calculatedValue = contents.reduce((total: number, item: any) => (
        total + item.item_price * item.quantity
    ), 0)
    const value = Number(payload.value ?? calculatedValue)
    const eventId = payload.event_id ? `${payload.event_id}` : undefined
    const purchaseStorageKey = eventId && metaEventName === 'Purchase'
        ? `${metaPurchaseStoragePrefix}${eventId}`
        : undefined

    if (purchaseStorageKey && window.localStorage.getItem(purchaseStorageKey)) return

    const parameters = {
        content_ids: contentIds,
        contents,
        content_type: 'product',
        value: Number.isFinite(value) ? value : 0,
        currency: 'MXN',
        ...(items.length === 1 && items[0].item_name ? { content_name: items[0].item_name } : {}),
        ...(contents.length > 0 ? {
            num_items: contents.reduce((total: number, item: any) => total + item.quantity, 0)
        } : {})
    }

    if (eventId) window.fbq('track', metaEventName, parameters, { eventID: eventId })
    else window.fbq('track', metaEventName, parameters)

    if (purchaseStorageKey) window.localStorage.setItem(purchaseStorageKey, '1')
}

/** Dispatches a Meta-only event when Segment/GA4 already emitted it server-side. */
export const trackMeta = (eventName: string, properties: AnalyticsProperties = {}) => {
    if (!isBrowser()) return

    try {
        dispatchMeta(eventName, getEventPayload(properties))
    } catch (error) {
        logAnalyticsError('Meta Pixel event', error)
    }
}

export const track = (eventName: string, properties: AnalyticsProperties = {}) => {
    if (!isBrowser()) return

    const payload: AnalyticsProperties = getEventPayload(properties)
    if (metaEventMap[eventName] && !payload.event_id) {
        // Segment receives this same ID, so an externally configured CAPI
        // destination can deduplicate its server copy against the browser Pixel.
        payload.event_id = `${toSnakeCase(eventName)}-${createId()}`
    }

    try {
        if (window.analytics && typeof window.analytics.track === 'function') {
            window.analytics.track(eventName, payload)
        }
    } catch (error) {
        logAnalyticsError('Segment track', error)
    }

    try {
        if (typeof window.gtag === 'function') {
            window.gtag('event', ga4EventMap[eventName] || toSnakeCase(eventName), toGa4Payload(payload))
        }
    } catch (error) {
        logAnalyticsError('GA4 event', error)
    }

    try {
        dispatchMeta(eventName, payload)
    } catch (error) {
        logAnalyticsError('Meta Pixel event', error)
    }
}

export const page = (name: string, properties: AnalyticsProperties = {}) => {
    if (!isBrowser()) return

    const payload = getEventPayload({ name, ...properties })

    try {
        if (window.analytics && typeof window.analytics.page === 'function') {
            window.analytics.page(name, payload)
        }
    } catch (error) {
        logAnalyticsError('Segment page', error)
    }

    try {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
                page_title: name,
                page_location: payload.page_url,
                page_path: payload.path,
                ...payload
            })
        }
    } catch (error) {
        logAnalyticsError('GA4 page', error)
    }
}

export const identify = (userId: string, traits: AnalyticsProperties = {}) => {
    if (!isBrowser() || !userId) return

    try {
        if (window.analytics && typeof window.analytics.identify === 'function') {
            window.analytics.identify(userId, traits)
        }
    } catch (error) {
        logAnalyticsError('Segment identify', error)
    }

    try {
        if (typeof window.gtag === 'function') {
            window.gtag('set', { user_id: userId })
        }
    } catch (error) {
        logAnalyticsError('GA4 identify', error)
    }
}

export const reset = () => {
    if (!isBrowser()) return

    try {
        if (window.analytics && typeof window.analytics.reset === 'function') {
            window.analytics.reset()
        }
    } catch (error) {
        logAnalyticsError('Segment reset', error)
    }

    window.localStorage.removeItem(cartIdKey)
    window.localStorage.setItem(cartIdKey, createId())
}

export default {
    track,
    page,
    identify,
    reset,
    getTrackingContext,
    getTrackingIds,
    normalizeAnalyticsItem,
    normalizeAnalyticsItems,
    trackMeta
}
